import { call, put, race, SagaGenerator, select, take } from "typed-redux-saga";
import { AnyAction } from "typescript-fsa";
import _ from "lodash";

import { Activity, ActivityActions, ActivitySelectors } from "@com.mgmtp.a12.client/client-core/lib/core/activity";
import { Commands } from "@com.mgmtp.a12.formengine/formengine-core/lib/back-end/store";
import {
	FormActivity,
	FormEngineActions,
	FormEngineSelectors
} from "@com.mgmtp.a12.formengine/formengine-core/lib/client-extensions";

import { isDocumentWithIdAndProcessMetadata } from "../modules/workflows/data/processMetadata";
import { TASK_DETAIL_ACTIVITY_ID, ANTRAG_DETAIL_ACTIVITY_ID } from "../modules/processOverview/store/actions";

import { reloadTaskAndAntragActivity } from "./workflowUtils";

export function isWorkflowActivity(activity: Activity | undefined): boolean {
	return activity?.descriptor?.taskId !== undefined;
}

export function isWorkflowActivityWithLoadedData(activity: Activity): boolean {
	return activity && Activity.findDefaultDataHolder(activity)?.loadingState === "loaded";
}

/**
 * Validates an activity.
 * Returns false if the activity is NOT valid.
 * Otherwise, it returns true.
 */
export function* validateActivity(activityId: string): SagaGenerator<boolean> {
	return yield* call(validateActivityWithCommand, activityId, Commands.validateFull());
}

export function* validateActivityWithCommand(activityId: string, validationCommand: AnyAction): SagaGenerator<boolean> {
	yield* put(
		FormEngineActions.command({
			activityId,
			engineEvent: validationCommand
		})
	);
	return yield* call(isValidDocument, activityId);
}

export function* saveActivity(activityId: string): SagaGenerator<boolean> {
	yield* put(ActivityActions.save.started({ activityId }));
	const saveResultAction = yield* race({
		success: take(function (action: AnyAction): boolean {
			return ActivityActions.save.done.match(action) && action.payload.params.activityId === activityId;
		}),
		failed: take(function (action: AnyAction): boolean {
			return ActivityActions.save.failed.match(action) && action.payload.params.activityId === activityId;
		})
	});
	return saveResultAction.success !== undefined;
}

export function* reloadActivity(activity: Activity): SagaGenerator<void> {
	if (isWorkflowActivity(activity)) {
		yield* call(reloadTaskAndAntragActivity);
	} else {
		yield* put(ActivityActions.reloadData({ activityId: activity.id }));
	}
}

/**
 * Checks if the data of an activity is valid.
 * */
export function* isValidDocument(activityId: string): SagaGenerator<boolean> {
	const uiState = yield* select(FormEngineSelectors.uiState(activityId));
	return !uiState || Object.keys(uiState.messages).length === 0;
}

export function getData<T>(activity: Activity): T | undefined {
	const dataHolder = activity.dataHolders?.find(Activity.DataHolder.hasDescriptor(activity.descriptor)) as
		{ data: T } | undefined;
	return dataHolder?.data;
}

export function getWorkflowsData(activity: Activity): FormActivity.Data.SingleDocumentData | undefined {
	const dataHolder = Activity.findDefaultDataHolder(activity);
	if (dataHolder?.loadingState !== "loaded") {
		console.error("Data is not loaded yet!");
		return undefined;
	} else if (
		!FormActivity.Data.SingleDocumentData.isInstance(dataHolder.data) ||
		!isDocumentWithIdAndProcessMetadata(dataHolder.data.document)
	) {
		console.error("Data is not suitable!");
		return undefined;
	}
	return dataHolder.data;
}

export function getFormActivityData(activity: Activity): FormActivity.Data.SingleDocumentData | undefined {
	const dataHolder = Activity.findDefaultDataHolder(activity);
	if (dataHolder?.loadingState !== "loaded") {
		console.error("Data is not loaded yet!");
		return undefined;
	} else if (!FormActivity.Data.SingleDocumentData.isInstance(dataHolder.data)) {
		console.error("Data is not SingleDocumentData!");
		return undefined;
	}
	return dataHolder.data;
}

export function* mergeData(activityId: string, newData: object) {
	yield* put(
		ActivityActions.setData({
			activityId,
			data: _.merge(yield* select(ActivitySelectors.data(activityId)), newData)
		})
	);
}

export function isTaskDetailActivityId(activityId: string): boolean {
	return activityId === TASK_DETAIL_ACTIVITY_ID;
}

export function isAntragDetailActivityId(activityId: string): boolean {
	return activityId === ANTRAG_DETAIL_ACTIVITY_ID;
}
